import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { ResponsiveBar } from '@nivo/bar';
import * as reportService from '../services/reportService';

export function TimeTrackingReport(): React.ReactElement {
  const { token, user } = useAuth();
  const isManager = user?.role === 'platform_admin' || user?.role === 'company_admin' || user?.role === 'project_manager' || user?.role === 'project_manager_lite';
  const [reportData, setReportData] = useState<reportService.TimeTrackingReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedSprintId, setSelectedSprintId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Collapsible project groups
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const loadReport = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const data = await reportService.getTimeTrackingReport(
        token,
        selectedSprintId || undefined,
        startDate || undefined,
        endDate || undefined,
        selectedUserId || undefined
      );
      setReportData(data);
      // Auto-expand all projects on load
      const projectIds = new Set(data.entries.map(e => e.project_id));
      setExpandedProjects(projectIds);
    } catch (err: any) {
      console.error('Error loading time tracking report:', err);
      setError('Failed to load time tracking report');
    } finally {
      setLoading(false);
    }
  }, [token, selectedSprintId, selectedUserId, startDate, endDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // When sprint is selected, auto-fill date range
  const handleSprintChange = (sprintId: string) => {
    setSelectedSprintId(sprintId);
    if (sprintId && reportData) {
      const sprint = reportData.sprints.find(s => s.id === sprintId);
      if (sprint) {
        setStartDate(sprint.start_date.split('T')[0]);
        setEndDate(sprint.end_date.split('T')[0]);
      }
    }
  };

  const clearFilters = () => {
    setSelectedSprintId('');
    setSelectedUserId('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = selectedSprintId || selectedUserId || startDate || endDate;

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Group entries by project
  const groupedData = useMemo(() => {
    if (!reportData) return [];
    const map = new Map<string, {
      project_id: string;
      project_name: string;
      project_code: string;
      entries: reportService.TimeTrackingEntry[];
      subtotal: number;
    }>();

    for (const entry of reportData.entries) {
      if (!map.has(entry.project_id)) {
        map.set(entry.project_id, {
          project_id: entry.project_id,
          project_name: entry.project_name,
          project_code: entry.project_code,
          entries: [],
          subtotal: 0,
        });
      }
      const group = map.get(entry.project_id)!;
      group.entries.push(entry);
      group.subtotal += parseFloat(String(entry.hours));
    }

    // Sort projects alphabetically, entries by date ASC within each
    const groups = Array.from(map.values()).sort((a, b) =>
      a.project_name.localeCompare(b.project_name)
    );
    for (const g of groups) {
      g.entries.sort((a, b) =>
        new Date(a.logged_date).getTime() - new Date(b.logged_date).getTime()
      );
      g.subtotal = Math.round(g.subtotal * 100) / 100;
    }
    return groups;
  }, [reportData]);

  // KPI calculations
  const kpis = useMemo(() => {
    if (!reportData || reportData.entries.length === 0) {
      return { totalHours: 0, projectCount: 0, daysActive: 0 };
    }
    const projectIds = new Set(reportData.entries.map(e => e.project_id));
    const dates = new Set(reportData.entries.map(e => e.logged_date.split('T')[0]));
    return {
      totalHours: reportData.total_hours,
      projectCount: projectIds.size,
      daysActive: dates.size,
    };
  }, [reportData]);

  // Chart data: daily hours stacked by project
  const chartData = useMemo(() => {
    if (!reportData || reportData.entries.length === 0) return { data: [], keys: [] };

    // Aggregate hours per date per project
    const dateProjectMap = new Map<string, Record<string, number>>();
    const projectNames = new Set<string>();

    for (const entry of reportData.entries) {
      const date = entry.logged_date.split('T')[0];
      if (!dateProjectMap.has(date)) {
        dateProjectMap.set(date, {});
      }
      const dayData = dateProjectMap.get(date)!;
      const projName = entry.project_name;
      projectNames.add(projName);
      dayData[projName] = (dayData[projName] || 0) + parseFloat(String(entry.hours));
    }

    // Sort dates ascending
    const sortedDates = Array.from(dateProjectMap.keys()).sort();
    const keys = Array.from(projectNames).sort();

    const data = sortedDates.map(date => {
      const row: Record<string, any> = {
        date: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      };
      const dayData = dateProjectMap.get(date)!;
      for (const key of keys) {
        row[key] = Math.round((dayData[key] || 0) * 100) / 100;
      }
      return row;
    });

    return { data, keys };
  }, [reportData]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Color palette for chart bars
  const chartColors = ['#0d9488', '#0891b2', '#4f46e5', '#7c3aed', '#db2777', '#ea580c', '#65a30d', '#0284c7'];

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Time Tracking Report</h1>
          <p className="text-blue-100 mt-1">Review logged hours by sprint or date range</p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Filter Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sprint</label>
              <select
                value={selectedSprintId}
                onChange={(e) => handleSprintChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Sprints</option>
                {reportData?.sprints.map(s => {
                  const start = new Date(s.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const end = new Date(s.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  return (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.status}) — {start} – {end}
                    </option>
                  );
                })}
              </select>
            </div>
            {isManager && reportData && reportData.users.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Person</label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">All People</option>
                  {reportData.users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 font-medium pb-2"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : reportData && reportData.entries.length > 0 ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Hours</p>
                <p className="text-3xl font-bold text-teal-600 dark:text-teal-400">
                  {kpis.totalHours}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">hours logged</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Projects</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {kpis.projectCount}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">with logged time</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">Days Active</p>
                <p className="text-3xl font-bold text-cyan-600 dark:text-cyan-400">
                  {kpis.daysActive}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">unique days</p>
              </div>
            </div>

            {/* Daily Hours Bar Chart */}
            {chartData.data.length > 1 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Daily Hours</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Hours logged per day, stacked by project</p>
                </div>
                <div className="px-6 py-4" style={{ height: 400 }}>
                  <ResponsiveBar
                    data={chartData.data}
                    keys={chartData.keys}
                    indexBy="date"
                    margin={{ top: 20, right: 30, bottom: 60, left: 50 }}
                    padding={0.3}
                    groupMode="stacked"
                    colors={chartColors.slice(0, chartData.keys.length)}
                    borderRadius={2}
                    axisBottom={{
                      tickRotation: chartData.data.length > 10 ? -45 : 0,
                      legendOffset: 50,
                      legendPosition: 'middle' as const,
                    }}
                    axisLeft={{
                      legend: 'Hours',
                      legendOffset: -40,
                      legendPosition: 'middle' as const,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    labelTextColor="#ffffff"
                    tooltip={({ id, value, indexValue, color }) => (
                      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{indexValue}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                          <span className="text-gray-700 dark:text-gray-300">{id}:</span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{value}h</span>
                        </div>
                      </div>
                    )}
                    legends={chartData.keys.length <= 6 ? [
                      {
                        dataFrom: 'keys' as const,
                        anchor: 'top-right' as const,
                        direction: 'column' as const,
                        translateX: 0,
                        translateY: -10,
                        itemWidth: 120,
                        itemHeight: 18,
                        symbolSize: 10,
                        symbolShape: 'circle' as const,
                      },
                    ] : []}
                    theme={{
                      axis: {
                        ticks: { text: { fill: '#9ca3af', fontSize: 11 } },
                        legend: { text: { fill: '#9ca3af', fontSize: 12 } },
                      },
                      grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
                      legends: { text: { fill: '#6b7280', fontSize: 11 } },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Grouped Time Entries Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Time Entries</h3>
              </div>

              {groupedData.map((group) => (
                <div key={group.project_id} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                  {/* Project header */}
                  <button
                    onClick={() => toggleProject(group.project_id)}
                    className="w-full flex items-center justify-between px-6 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <svg
                        className={`w-4 h-4 text-gray-500 transition-transform ${expandedProjects.has(group.project_id) ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{group.project_name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({group.project_code})</span>
                    </div>
                    <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">{group.subtotal}h</span>
                  </button>

                  {/* Project entries */}
                  {expandedProjects.has(group.project_id) && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700/30">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Task</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Person</th>
                            <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hours</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {group.entries.map((entry) => (
                            <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {formatDate(entry.logged_date.split('T')[0])}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
                                {entry.requirement_title}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm">
                                {entry.requirement_code ? (
                                  <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                    {entry.requirement_code}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                {entry.user_name}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-gray-100">
                                {entry.hours}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                {entry.description || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="px-6 py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30">
                        Subtotal: <span className="text-teal-600 dark:text-teal-400 font-semibold">{group.subtotal}h</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Grand Total */}
              <div className="px-6 py-4 bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600">
                <div className="flex justify-end items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total:</span>
                  <span className="text-lg font-bold text-teal-600 dark:text-teal-400">{reportData.total_hours}h</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No time entries found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {hasActiveFilters
                ? 'Try adjusting your filters to see time entries.'
                : 'Log time on tasks from the task detail page to see data here.'}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TimeTrackingReport;
